import {
    SparePartGroupList,
    SpareParts,
    SparePartsResponseError,
} from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { SparePartList } from '@ecs-features/result/type-designation-result/type-designation-result.component';
import { PackageInformation } from '@os-components';

export enum SearchResultType {
    SERIALNUMBER = 'SERIALNUMBER',
    SPARE_PARTS = 'SPARE_PARTS',
    SPARE_PARTS_DATA = 'SPARE_PARTS_DATA',
    FREE_TEXT = 'FREE_TEXT',
    FREE_TEXT_DATA = 'FREE_TEXT_DATA',
    TYPE_DESIGNATION = 'TYPE_DESIGNATION',
    TYPE_DESIGNATION_DATA = 'TYPE_DESIGNATION_DATA',
    SPARE_PART_LIST = 'SPARE_PART_LIST',
}

export interface SerialNumberSearchResult {
    type: SearchResultType.SERIALNUMBER;
    catalogDescription: string;
    productName: string;
    serialNumber: string;
    pictureUrl?: string;
    isCabinetInverter: boolean;
    isIndustrialGear: boolean;
    isFunctionalSafety: boolean;
    isAtex: boolean;
    isDtcKit: boolean;
    spareParts?: SparePartGroupList | null;
    sparePartsError?: SparePartsResponseError | null;
    productLifeCycleStatus?: string;
    isOrderable: boolean;
    packageInformation?: PackageInformation | null;
}

export interface SparePartListSearchResult {
    type: SearchResultType.SPARE_PART_LIST;
    productName: string;
    id: string;
    updated: boolean;
    oldId: string;
    spareParts?: SparePartGroupList | null;
    sparePartsError?: SparePartsResponseError | null;
}

export interface SparePartsSearchResult {
    type: SearchResultType.SPARE_PARTS;
    /**
     * List of spare part IDs that are available for display.
     * Only present if there actually are IDs for display.
     */
    spareParts?: string[];
    /**
     * List of spare part IDs that resulted in an error (no result).
     * Only present if there are errors.
     */
    errors?: string[];
}

export interface FreeTextSearchResult {
    type: SearchResultType.FREE_TEXT;
    /**
     * List of spare part IDs that are available for display.
     * Only present if there actually are IDs for display.
     */
    spareParts: string[];

    /**
     * Flag which is true, if there are more search results than were returned by backend
     */
    limitExceeded: boolean;
}

export interface TypeDesignationSearchResult {
    type: SearchResultType.TYPE_DESIGNATION;
    /**
     * List of spare part IDs that are available for display.
     * Only present if there actually are IDs for display.
     * Result of free text search
     */
    spareParts?: string[];
    /**
     * Flag which is true, if there are more search results than were returned by backend
     */
    limitExceeded: boolean;

    sparePartLists?: SparePartList[];
}

export interface FreeTextDataResult {
    type: SearchResultType.FREE_TEXT_DATA;
    /**
     * List of spare part IDs that are available for display.
     * Only present if there actually are IDs for display.
     */
    spareParts: SpareParts;
}

export interface SparePartsDataResult {
    type: SearchResultType.SPARE_PARTS_DATA;
    /**
     * List of spare part objects
     */
    spareParts: SpareParts;
    errors?: string[];
}

export interface TypeDesignationDataResult {
    type: SearchResultType.TYPE_DESIGNATION_DATA;
    /**
     * List of spare part objects
     */
    spareParts?: SpareParts;
    sparePartLists?: SparePartList[];
}

export interface SearchError {
    content: string;
    followUpLink?: {
        href: string;
        text: string;
    };
    orderNumberItemsChecked?: boolean;
}

// TODO: will include other search result types as well
/**
 * Search result as returned by the backend
 */
export type SearchResultResponse =
    | SerialNumberSearchResult
    | SparePartListSearchResult
    | SparePartsSearchResult
    | FreeTextSearchResult
    | TypeDesignationSearchResult
    | TypeDesignationDataResult
    | SparePartsDataResult
    | FreeTextDataResult;

/**
 * Search result that may also be `null` in case of an error
 */
export type SearchResult = SearchResultResponse | null | undefined;
